import { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import CameraCapture from '../components/CameraCapture';
import { calcRental } from '../utils/calcRental';
import ContractDocument from '../components/ContractDocument';
import { buildContractContext } from '../utils/contractContext';
import { renderContract, printContract } from '../utils/renderContract';
import { invoiceDisplayNumber } from '../utils/invoiceDoc';
import { printInvoice } from './InvoiceViewModal';

const CI_STEPS = ['Location', 'Extras & Insurance', 'ID & Photos', 'Sign & Pay'];

const COND_OPTIONS = [
  { value: 'no_damage',     label: '✅ No damage' },
  { value: 'small_scratch', label: '🔍 Scratch' },
  { value: 'damage',        label: '⚠️ Damage' },
  { value: 'major_damage',  label: '🚨 Major' },
];

const FALLBACK_CLAUSES = [
  'The Renter agrees to use the vehicle only for lawful purposes and shall not sub-let or transfer the vehicle to any third party.',
  'The vehicle shall be returned in the same condition as received, except for normal wear and tear. Any damage incurred during the rental period is the responsibility of the Renter.',
  'Fuel policy: Return full. Refuelling charges apply if vehicle is returned below the agreed fuel level.',
  'Speed limits and traffic laws of Cyprus must be observed at all times. The Renter is responsible for all fines, penalties, and road tolls incurred during the rental period.',
  'Any damage beyond the insured excess will be charged to the Renter\'s credit card or deposit.',
  'Late returns will be charged at the agreed daily rate, pro-rated to the nearest hour.',
  'The Renter must report any accident or damage immediately to the rental company and must not admit liability to any third party.',
];

export default function CheckInModal() {
  const {
    modals, closeModal, openModal,
    rentalDraft, setRentalDraft,
    rentals, setRentals, setBookings,
    locations, extras, insurance,
    vehicles, addRental, addInvoice, updateInvoice, nextInvoiceNum, showToast,
    invoices, companySettings, customers,
    agreementTemplates, groups,
    currentUserId, getUserName,
    getDamagesForVehicle, getDamagesForRental, updateDamage,
  } = useApp();

  const open = modals['check-in'];

  // Resolve which agreement template to use for a vehicle
  function getAgreementForVehicle(vid) {
    const v = vehicles.find(x => x.id === vid);
    if (!v) return null;
    if (v.agreementId) {
      const tmpl = agreementTemplates.find(t => t.id === v.agreementId);
      if (tmpl) return tmpl;
    }
    if (v.groupId) {
      const grp = groups.find(g => g.id === v.groupId);
      if (grp?.agreementId) {
        const tmpl = agreementTemplates.find(t => t.id === grp.agreementId);
        if (tmpl) return tmpl;
      }
    }
    return agreementTemplates.find(t => t.isDefault) || agreementTemplates[0] || null;
  }

  const [step, setStep]               = useState(1);
  const [pickupLocId, setPickupLocId]  = useState('');
  const [returnLocId, setReturnLocId]  = useState('');
  const [diffReturn, setDiffReturn]    = useState(false);
  const [customPickup, setCustomPickup] = useState('');
  const [selectedExtras, setSelectedExtras]     = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState('');
  const [ciPassport, setCiPassport]    = useState('');
  const [ciLicence, setCiLicence]      = useState('');
  const [ciLicExpiry, setCiLicExpiry]  = useState('');
  const [ciPassportPhoto, setCiPassportPhoto] = useState(null);
  const [ciLicencePhoto, setCiLicencePhoto]   = useState(null);
  const [passportChecked, setPassportChecked] = useState(false);
  const [licenceChecked,  setLicenceChecked]  = useState(false);
  const [depositChecked,  setDepositChecked]  = useState(false);
  const [ciCond, setCiCond]            = useState('no_damage');
  const [ciCondNote, setCiCondNote]    = useState('');
  const [ciSigned, setCiSigned]        = useState(false);
  const [ciPayMethod, setCiPayMethod]  = useState('cash');
  const [camera, setCamera]            = useState(null);
  const [showFullAgreement, setShowFullAgreement] = useState(false);
  const [pickupPhotos, setPickupPhotos] = useState({});
  const [activeSlot, setActiveSlot]    = useState(null);

  const canvasRef         = useRef(null);
  const drawingRef        = useRef(false);
  const lastPosRef        = useRef(null);
  const passportFileRef   = useRef(null);
  const licenceFileRef    = useRef(null);
  const pickupPhotoRef    = useRef(null);

  const defaultLoc = locations.find(l => l.isDefault) || locations[0];

  useEffect(() => {
    if (open && rentalDraft) {
      setStep(1);
      // Pre-fill location from booking if it exists, else default
      setPickupLocId(rentalDraft.pickupLocId || defaultLoc?.id || '');
      setReturnLocId(rentalDraft.returnLocId && rentalDraft.returnLocId !== rentalDraft.pickupLocId ? rentalDraft.returnLocId : '');
      setDiffReturn(!!(rentalDraft.returnLocId && rentalDraft.returnLocId !== rentalDraft.pickupLocId));
      setCustomPickup(rentalDraft.customPickup || '');
      // Convert saved extras (array of names) back to { id: qty } object
      const initExtras = {};
      (rentalDraft.extras || []).forEach(nameOrId => {
        const ex = extras.find(e => e.name === nameOrId || e.id === nameOrId);
        if (ex) initExtras[ex.id] = (initExtras[ex.id] || 0) + 1;
      });
      setSelectedExtras(initExtras);
      const insPlans = (() => {
        const v = vehicles.find(x => x.id === rentalDraft.vid);
        if (v?.insuranceMode === 'custom' && v.customInsurance?.length > 0) return v.customInsurance;
        return insurance;
      })();
      setSelectedInsurance(
        rentalDraft.insuranceId ||
        insPlans.find(i => i.recommended)?.id ||
        insPlans[0]?.id || ''
      );
      setCiPassport(rentalDraft.passport   || '');
      setCiLicence(rentalDraft.licence    || '');
      setCiLicExpiry(rentalDraft.licExpiry || '');
      // Pre-load photos from booking engine
      setCiPassportPhoto(rentalDraft.passportPhoto || null);
      setCiLicencePhoto(rentalDraft.licencePhoto   || null);
      setPassportChecked(false);
      setLicenceChecked(false);
      setDepositChecked(false);
      setCiCond('no_damage');
      setCiCondNote('');
      setCiSigned(false);
      setCiPayMethod(rentalDraft.payMethod === 'bank' ? 'bank' : rentalDraft.payMethod || 'cash');
      setCamera(null);
      setShowFullAgreement(false);
      setPickupPhotos({});
    }
  }, [open]);

  const draft   = rentalDraft;
  const vehicle = draft ? vehicles.find(v => v.id === draft.vid) : null;
  const days    = draft ? Math.max(1, Math.round((new Date(draft.end) - new Date(draft.start)) / 86400000)) : 1;

  // Returns the correct insurance plans for this vehicle (custom or global)
  function getVehicleInsurancePlans(vid) {
    const v = vehicles.find(x => x.id === vid);
    if (v?.insuranceMode === 'custom' && v.customInsurance?.length > 0) return v.customInsurance;
    return insurance;
  }
  const vehicleInsPlans = getVehicleInsurancePlans(draft?.vid);

  const selectedIns = vehicleInsPlans.find(i => i.id === selectedInsurance);
  // Deposit always comes from the selected insurance plan (not original booking)
  const depositAmt  = parseFloat(selectedIns?.deposit || 0);

  function setExtraQty(id, qty, maxQty) {
    const clamped = Math.max(0, Math.min(qty, maxQty || 10));
    setSelectedExtras(prev => ({ ...prev, [id]: clamped }));
  }
  function toggleExtra(id) {
    setSelectedExtras(prev => ({ ...prev, [id]: (prev[id] || 0) > 0 ? 0 : 1 }));
  }

  const pickupLoc = locations.find(l => l.id === pickupLocId);
  const returnLoc = locations.find(l => l.id === returnLocId);
  const locFee    = (pickupLoc?.fee || 0) + (diffReturn ? (returnLoc?.fee || 0) : 0);

  const vatRate = parseFloat(companySettings?.vatRate || 19) / 100;
  const vatMode = companySettings?.vatMode || 'inclusive';

  const calc = calcRental({
    rate:          draft?.rate || 0,
    days,
    discount:      draft?.discount || 0,
    discType:      draft?.discType || 'eur',
    selectedIns,
    extrasConfig:  extras,
    selectedExtras,
    locFee,
    vatMode,
    vatRate,
    vehicleName:   vehicle?.name || 'Vehicle',
  });

  const { baseSub, insTotal, extraTotal, subtotal: subTotal, vatAmt: vat, grandTotal, lines: invLines } = calc;

  // Pre-paid detection.
  // alreadyPaid = the amount GENUINELY settled before check-in. Only PAID
  // invoices count — a draft / pending invoice (e.g. "cash on pickup" online
  // booking, or unverified bank) was NOT actually paid, so it doesn't reduce
  // what's due at the desk. Anything added at check-in (extras, insurance
  // upgrade) is therefore always collected at the desk as a SEPARATE charge.
  const linkedInvoices = (invoices || []).filter(inv => inv.contract === draft?.id || inv.contractId === draft?.id);
  const paidInvoices   = linkedInvoices.filter(inv => inv.status === 'paid');
  // The most-recent linked invoice (any status) — used only for display reference
  const existingInv    = paidInvoices[0] || linkedInvoices[0] || null;
  // A not-yet-paid linked invoice we can update in place instead of duplicating
  const draftInv       = linkedInvoices.find(inv => inv.status !== 'paid') || null;
  const diffOwed       = parseFloat(draft?.differenceOwed) || 0;

  let alreadyPaid, dueAtDesk;
  if (diffOwed > 0) {
    // Date-change extended a paid-online booking → original paid, only the diff due
    alreadyPaid = Math.max(0, grandTotal - diffOwed);
    dueAtDesk   = diffOwed;
  } else {
    // Sum everything actually paid so far. Extras added now → due at desk.
    alreadyPaid = paidInvoices.reduce((s, inv) => s + (parseFloat(inv.total) || 0), 0);
    dueAtDesk   = Math.max(0, grandTotal - alreadyPaid);
  }
  const isPrepaid = alreadyPaid > 0;
  const dueVat       = vatMode === 'inclusive'
    ? dueAtDesk - dueAtDesk / (1 + vatRate)
    : dueAtDesk * vatRate;

  // ── Itemized lines for the amount collected at the desk (additions beyond
  //    what was already paid online). Sums to dueAtDesk for fresh bookings. ──
  function computeDeskLines() {
    const lines = [];
    const origExtras = draft?.selectedExtras || {};
    const origInsId  = draft?.insuranceId || draft?.insuranceSelected;
    const origIns    = insurance.find(i => i.id === origInsId);
    const origInsTotal = origIns ? origIns.price * days : 0;
    const insDiff = (insTotal || 0) - origInsTotal;
    if (selectedIns && insDiff > 0.005) {
      lines.push({
        desc: origIns ? `Insurance upgrade — ${selectedIns.name}` : `Insurance — ${selectedIns.name}`,
        qty: days, unit: +(insDiff / days).toFixed(2), total: +insDiff.toFixed(2),
      });
    }
    extras.filter(e => (selectedExtras[e.id] || 0) > (origExtras[e.id] || 0)).forEach(ex => {
      const newQty  = ex.allowQty ? (selectedExtras[ex.id] || 1) : 1;
      const origQty = ex.allowQty ? (origExtras[ex.id] || 0) : 0;
      const diffQty = newQty - origQty;
      const lineTotal = +(((ex.perDay ? ex.price * days : ex.price) * diffQty).toFixed(2));
      lines.push({
        desc: ex.name + (diffQty > 1 ? ` ×${diffQty}` : ''),
        qty: ex.perDay ? days * diffQty : diffQty, unit: ex.price, total: lineTotal,
      });
    });
    return lines;
  }

  // ── Pro forma object for the desk-due amount (view / print) ──
  function buildDeskProforma() {
    const lines = computeDeskLines();
    const sub    = vatMode === 'inclusive' ? +(dueAtDesk / (1 + vatRate)).toFixed(2) : +dueAtDesk.toFixed(2);
    const vatAmt = vatMode === 'inclusive' ? +(dueAtDesk - sub).toFixed(2) : +(dueAtDesk * vatRate).toFixed(2);
    const todayLbl = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const methodLabel = { cash: 'Cash (at check-in)', stripe: 'Card / Stripe (at check-in)', bank: 'Bank Transfer (at check-in)', prepaid: 'Pre-paid' }[ciPayMethod] || ciPayMethod;
    return {
      num: null, status: 'pending',
      customer: draft.customer, email: draft.email || '',
      vehicle: vehicle?.name || '', contract: draft.id, vid: draft.vid,
      method: ciPayMethod, methodLabel,
      issue: todayLbl, issueDate: new Date().toISOString().slice(0, 10),
      lines, subtotal: sub, vatRate: Math.round(vatRate * 100), vatAmt,
      discount: 0, discType: 'eur', total: +dueAtDesk.toFixed(2),
      notes: `Additional charges collected at check-in · ${draft.id}`,
      vatMode,
    };
  }

  // ── Contract template + context (shared by inline View and Print) ──
  function getCheckinContract() {
    const tmpl = getAgreementForVehicle(draft?.vid);
    const ctx = buildContractContext({
      rental: {
        id: draft.id, customer: draft.customer,
        passport: ciPassport || draft.passport,
        licence: ciLicence || draft.licence,
        email: draft.email, phone: draft.phone,
        startLabel: draft.startLabel, endLabel: draft.endLabel,
        t1: draft.t1, t2: draft.t2,
        pickupLocName: pickupLoc?.name,
        returnLocName: diffReturn ? returnLoc?.name : pickupLoc?.name,
        calSpan: days, rate: draft.rate,
        deposit: depositAmt, depositMethod: ciPayMethod,
        agreedPrice: parseFloat(grandTotal.toFixed(2)),
        insurance: selectedIns?.name, insuranceExcess: selectedIns?.excess,
        extras: extras.filter(e => (selectedExtras[e.id] || 0) > 0).map(e => e.name),
      },
      vehicle, company: companySettings,
      signatureDataUrl: draft?.presignData || '',
      signedAt: draft?.presignedAt || '',
    });
    return { tmpl, ctx };
  }

  // Canvas signature
  function canvasPos(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * canvasRef.current.width  / rect.width,
      y: (e.clientY - rect.top)  * canvasRef.current.height / rect.height,
    };
  }
  function canvasDown(e) { drawingRef.current = true; lastPosRef.current = canvasPos(e); }
  function canvasMove(e) {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = canvasPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A3A5C'; ctx.lineWidth = 2; ctx.lineCap = 'round';
    ctx.stroke();
    lastPosRef.current = pos;
    setCiSigned(true);
  }
  function canvasUp() { drawingRef.current = false; }
  function clearCanvas() {
    canvasRef.current?.getContext('2d')?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setCiSigned(false);
  }

  function handlePhotoFile(e, setPhoto) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setPhoto(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  const PHOTO_SLOTS = ['Front', 'Rear', 'Left side', 'Right side', 'Dashboard', 'Interior'];

  function openSlotFile(label) {
    setActiveSlot(label);
    pickupPhotoRef.current?.click();
  }
  function onSlotFile(e) {
    const file = e.target.files?.[0];
    if (!file || !activeSlot) return;
    const reader = new FileReader();
    reader.onload = ev => setPickupPhotos(prev => ({ ...prev, [activeSlot]: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openSlotCamera(label) {
    setCamera({
      label: `${label} — Pickup`,
      setPhoto: (data) => setPickupPhotos(prev => ({ ...prev, [label]: data })),
    });
  }
  function removeSlotPhoto(label) {
    setPickupPhotos(prev => { const n = { ...prev }; delete n[label]; return n; });
  }

  function nextStep() {
    if (step === 3) {
      if (!passportChecked) { showToast('Please verify passport / ID'); return; }
      if (!licenceChecked)  { showToast('Please verify driving licence'); return; }
      if (depositAmt > 0 && !depositChecked) { showToast(`Please confirm deposit collection — €${depositAmt}`); return; }
    }
    setStep(s => s + 1);
  }

  function finalize() {
    if (!ciSigned && !draft?.presigned) { showToast('Customer signature is required'); return; }
    if (!draft) return;

    const signatureData = ciSigned && canvasRef.current
      ? canvasRef.current.toDataURL('image/png')
      : (draft.presignData || null);

    const finalPrice = parseFloat(grandTotal.toFixed(2));

    const checkedInData = {
      ...draft,
      status: 'active',
      passport: ciPassport || draft.passport,
      licence:  ciLicence  || draft.licence,
      licExpiry: ciLicExpiry || draft.licExpiry,
      passportPhoto: ciPassportPhoto || draft.passportPhoto || null,
      licencePhoto:  ciLicencePhoto  || draft.licencePhoto  || null,
      condPre: ciCond, condPreNote: ciCondNote,
      signed: true, checkedIn: true,
      signData: signatureData,
      checkInTime: new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      payMethod: draft.bankTransferVerified ? 'bank' : ciPayMethod,
      pickupLocId, pickupLocName: pickupLoc?.name || '',
      returnLocId:   diffReturn ? returnLocId   : pickupLocId,
      returnLocName: diffReturn ? (returnLoc?.name || '') : (pickupLoc?.name || ''),
      customPickup,
      extras: extras.filter(e => (selectedExtras[e.id] || 0) > 0).map(e => e.name),
      insurance: selectedIns?.name || '',
      insuranceId: selectedInsurance,
      agreedPrice: finalPrice,
      checkInPhotos: PHOTO_SLOTS.filter(l => pickupPhotos[l]).map(l => pickupPhotos[l]),
      agreementSaved: (() => {
        const tmpl = getAgreementForVehicle(draft.vid);
        const ctxForSnap = buildContractContext({
          rental: {
            id: draft.id,
            customer: draft.customer,
            passport: ciPassport || draft.passport,
            licence: ciLicence || draft.licence,
            email: draft.email, phone: draft.phone,
            nationality: draft.nationality,
            startLabel: draft.startLabel, endLabel: draft.endLabel,
            t1: draft.t1, t2: draft.t2,
            pickupLocName: pickupLoc?.name,
            returnLocName: diffReturn ? returnLoc?.name : pickupLoc?.name,
            calSpan: days, rate: draft.rate,
            deposit: depositAmt, depositMethod: ciPayMethod,
            agreedPrice: finalPrice,
            insurance: selectedIns?.name, insuranceExcess: selectedIns?.excess,
            extras: extras.filter(e => (selectedExtras[e.id] || 0) > 0).map(e => e.name),
          },
          vehicle: vehicle,
          company: companySettings,
          signatureDataUrl: signatureData,
          signedAt: new Date().toISOString(),
        });
        return {
        templateId: tmpl?.id || null,
        templateName: tmpl?.name || 'Rental Agreement',
        body: tmpl?.body || '',
        renderedHtml: tmpl ? renderContract(tmpl, ctxForSnap) : '',
        context: ctxForSnap,
        clauses: tmpl?.clauses || FALLBACK_CLAUSES,
        signedAt: new Date().toISOString(),
        signedAtFormatted: new Date().toLocaleString('en-GB'),
        customerName: draft.customer || '',
        passport: ciPassport || draft.passport || '',
        licence: ciLicence || draft.licence || '',
        vehicleName: vehicle?.name || '',
        vehiclePlate: vehicle?.plate || '',
        startLabel: draft.startLabel || '',
        endLabel: draft.endLabel || '',
        t1: draft.t1 || '',
        t2: draft.t2 || '',
        days,
        rate: draft.rate || 0,
        insuranceName: selectedIns?.name || '',
        insuranceExcess: selectedIns?.excess || 0,
        insurancePrice: selectedIns ? (selectedIns.price || 0) * days : 0,
        extras: extras.filter(e => (selectedExtras[e.id] || 0) > 0).map(e => e.name),
        deposit: depositAmt,
        depositMethod: ciPayMethod,
        agreedPrice: finalPrice,
        signData: signatureData,
        };
      })(),
      depositCollected: depositAmt,
      depositPayMethod: depositAmt > 0 ? ciPayMethod : null,
      deskPayMethod: ciPayMethod,
      // Assignment: record who did check-in, clear assignment for next phase
      checkedInBy: currentUserId,
      checkedInAt: new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
      assignedTo: null,
      assignPhase: null,
      // Cash tracking — record the amount ACTUALLY collected at the desk
      // (the due-at-desk balance, not the full total — the rest was prepaid).
      ...(ciPayMethod === 'cash' ? { cashCollectedBy: currentUserId, cashAmount: parseFloat((dueAtDesk > 0 ? dueAtDesk : finalPrice).toFixed(2)) } : {}),
    };

    const existingRental = rentals.find(r => r.id === draft.id);
    if (existingRental) {
      setRentals(prev => prev.map(r => r.id === draft.id ? checkedInData : r));
      setBookings(prev => prev.map(b => b.rentalId === draft.id
        ? { ...b, type: 'rental', cls: 'cb-r' }
        : b
      ));
    } else {
      addRental(checkedInData);
    }

    const today   = new Date();
    const fmtDate = d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const isoDate = d => d.toISOString().split('T')[0];
    const dueDate = new Date(today.getTime() + 7 * 86400000);
    const PAY_LABEL = { cash: 'Cash', stripe: 'Stripe · Card', bank: 'Bank Transfer', prepaid: 'Pre-paid' };

    // invLines and subtotal come from calcRental() (already computed above)
    const subtotal = calc.subtotalExVat;

    // Pre-paid booking — a PAID invoice already covers part/all of the total
    // (online card paid at booking, or verified bank transfer).
    if (alreadyPaid > 0) {
      const dueAtDeskAmt = Math.max(0, grandTotal - alreadyPaid);
      if (dueAtDeskAmt > 0) {
        // Build lines for ONLY new/changed items vs original booking
        const addLines = [];
        const origExtras = draft.selectedExtras || {};

        // Insurance: show only the upgrade difference
        const origInsId = draft.insuranceId || draft.insuranceSelected;
        const origIns = insurance.find(i => i.id === origInsId);
        const origInsTotal = origIns ? origIns.price * days : 0;
        const insDiff = insTotal - origInsTotal;
        if (selectedIns && insDiff > 0) {
          const label = origIns
            ? `Insurance upgrade — ${selectedIns.name} (from ${origIns.name})`
            : `Insurance — ${selectedIns.name}`;
          addLines.push({ desc: label, qty: days, unit: parseFloat((insDiff / days).toFixed(2)), total: parseFloat(insDiff.toFixed(2)) });
        }

        // Extras: only newly added items not in original booking
        extras.filter(e => {
          const newQty = selectedExtras[e.id] || 0;
          const origQty = origExtras[e.id] || 0;
          return newQty > origQty;
        }).forEach(ex => {
          const newQty  = ex.allowQty ? (selectedExtras[ex.id] || 1) : 1;
          const origQty = ex.allowQty ? (origExtras[ex.id] || 0) : 0;
          const diffQty = newQty - origQty;
          const lineTotal = parseFloat(((ex.perDay ? ex.price * days : ex.price) * diffQty).toFixed(2));
          addLines.push({ desc: ex.name + (diffQty > 1 ? ` ×${diffQty}` : ''), qty: ex.perDay ? days * diffQty : diffQty, unit: ex.price, total: lineTotal });
        });
        const addVat = vatMode === 'inclusive'
          ? dueAtDeskAmt - dueAtDeskAmt / (1 + vatRate)
          : dueAtDeskAmt * vatRate;
        const addSubtotal = vatMode === 'inclusive'
          ? parseFloat((dueAtDeskAmt / (1 + vatRate)).toFixed(2))
          : parseFloat(dueAtDeskAmt.toFixed(2));
        const extInvNum = nextInvoiceNum();
        const deskMethodLabel = {
          cash:   'Cash (at check-in)',
          stripe: 'Card / Stripe (at check-in)',
          bank:   'Bank Transfer (at check-in)',
        }[ciPayMethod] || ciPayMethod;
        // Cash/card collected at desk = paid immediately; bank = still pending
        const deskInvStatus = ciPayMethod === 'bank' ? 'pending' : 'paid';
        addInvoice({
          id: extInvNum, num: extInvNum,
          customer: draft.customer, email: draft.email || '', addr: '',
          vehicle: vehicle?.name || '', contract: draft.id, contractId: draft.id, vid: draft.vid,
          method: ciPayMethod, methodLabel: deskMethodLabel,
          issue: fmtDate(today), due: fmtDate(dueDate),
          issueDate: isoDate(today), dueDate: isoDate(dueDate),
          lines: addLines, subtotal: addSubtotal,
          vatRate: Math.round(vatRate * 100), vatAmt: parseFloat(addVat.toFixed(2)),
          discount: 0, discType: 'eur',
          total: parseFloat(dueAtDeskAmt.toFixed(2)),
          notes: 'Additional charges at check-in', status: deskInvStatus, source: 'extras',
        });
        setRentalDraft(null);
        closeModal('check-in');
        showToast(`✅ ${draft.id} checked in — additional pro forma created`);
      } else {
        setRentalDraft(null);
        closeModal('check-in');
        showToast(`✅ ${draft.id} checked in`);
      }
      return;
    }

    // Nothing paid yet. If an unpaid pro forma already exists (e.g. a
    // "cash on pickup" online booking created a draft), UPDATE it to the final
    // total + desk payment method instead of creating a duplicate. Otherwise
    // create a fresh pro forma for the full amount.
    const deskStatus = ciPayMethod === 'bank' ? 'pending' : 'paid';
    if (draftInv && updateInvoice) {
      updateInvoice(draftInv.id, {
        method: ciPayMethod, methodLabel: PAY_LABEL[ciPayMethod] || ciPayMethod,
        issue: fmtDate(today), due: fmtDate(dueDate),
        issueDate: isoDate(today), dueDate: isoDate(dueDate),
        lines: invLines, subtotal,
        vatRate: 19, vatAmt: parseFloat(vat.toFixed(2)),
        discount: draft.discount || 0, discType: draft.discType || 'eur',
        total: finalPrice, status: deskStatus, source: 'checkin',
      });
    } else {
      const invNum = nextInvoiceNum();
      addInvoice({
        id: invNum, num: invNum,
        customer: draft.customer, email: draft.email || '', addr: '',
        vehicle: vehicle?.name || '', contract: draft.id, vid: draft.vid,
        method: ciPayMethod, methodLabel: PAY_LABEL[ciPayMethod] || ciPayMethod,
        issue: fmtDate(today), due: fmtDate(dueDate),
        issueDate: isoDate(today), dueDate: isoDate(dueDate),
        lines: invLines, subtotal,
        vatRate: 19, vatAmt: parseFloat(vat.toFixed(2)),
        discount: draft.discount || 0, discType: draft.discType || 'eur',
        total: finalPrice, notes: '', status: deskStatus, source: 'auto',
      });
    }

    setRentalDraft(null);
    closeModal('check-in');
    showToast(`✅ ${draft.id} checked in — pro forma created`);
  }

  if (!open || !draft) return null;

  const Row = ({ label, val }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: 'var(--tx2)', fontSize: 11 }}>
      <span>{label}</span><span>€{typeof val === 'number' ? val.toFixed(2) : val}</span>
    </div>
  );

  const DocPhoto = ({ icon, label, photo, setPhoto, fileRef }) => (
    <div style={{ border: `1px solid ${photo ? 'var(--gr-b)' : 'var(--bd)'}`, borderRadius: 'var(--r)', padding: 12, background: 'var(--sur2)' }}>
      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
        {icon} {label}
        {photo && <span style={{ fontSize: 9, background: 'var(--gr)', color: '#fff', padding: '1px 6px', borderRadius: 8, marginLeft: 4 }}>✓ Photo</span>}
      </div>
      {photo ? (
        <>
          <img src={photo} alt={label}
            style={{ width: '100%', maxHeight: 130, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--bd)', marginBottom: 8, display: 'block' }} />
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📁 Replace</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCamera({ label, setPhoto })}>📷 Retake</button>
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)' }} onClick={() => setPhoto(null)}>✕ Remove</button>
          </div>
        </>
      ) : (
        <>
          <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--tx3)', fontSize: 28 }}>
            {icon}<div style={{ fontSize: 11, marginTop: 4 }}>No photo yet</div>
          </div>
          <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()}>📁 Upload</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setCamera({ label, setPhoto })}>📷 Camera</button>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={e => handlePhotoFile(e, setPhoto)} />
    </div>
  );

  const isoNow = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      <Modal open={!!open} size="lg" onClose={() => closeModal('check-in')}>
        <ModalHeader
          title="Vehicle Check-in"
          subtitle={`${draft.id} · ${draft.customer} · ${vehicle?.name?.split('–')[0].trim() || ''}`}
          onClose={() => closeModal('check-in')}
        />

        {/* Step tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--bd)' }}>
          {CI_STEPS.map((s, i) => (
            <div key={i} style={{
              flex: 1, padding: '9px 4px', textAlign: 'center', fontSize: 10, fontWeight: 500,
              borderBottom: `2px solid ${step === i + 1 ? 'var(--ac)' : step > i + 1 ? 'var(--gr)' : 'transparent'}`,
              color: step === i + 1 ? 'var(--ac)' : step > i + 1 ? 'var(--gr)' : 'var(--tx3)',
            }}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        <ModalBody>

          {/* ── STEP 1: Location ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Pre-filled notice if from booking */}
              {draft.pickupLocName && (
                <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '8px 12px', fontSize: 11 }}>
                  📌 Pre-filled from online booking — confirm or change below
                </div>
              )}

              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Pickup Location</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8 }}>
                  {locations.map(loc => (
                    <div key={loc.id} onClick={() => setPickupLocId(loc.id)} style={{
                      border: `1.5px solid ${pickupLocId === loc.id ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', padding: '10px 12px', cursor: 'pointer',
                      background: pickupLocId === loc.id ? 'var(--ac-l)' : 'var(--sur)', fontSize: 11,
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 3 }}>📍 {loc.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{loc.address}</div>
                      <div style={{ fontSize: 10, marginTop: 4, color: loc.fee > 0 ? 'var(--am)' : 'var(--gr)', fontWeight: 600 }}>
                        {loc.fee > 0 ? `+€${loc.fee} delivery fee` : 'Free pickup'}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="fg" style={{ marginTop: 10 }}>
                  <label className="fl">Custom pickup address (optional)</label>
                  <input type="text" className="fi" value={customPickup} onChange={e => setCustomPickup(e.target.value)} placeholder="e.g. Hotel Elysium, Paphos" />
                </div>
              </div>

              <div onClick={() => setDiffReturn(d => !d)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <div style={{
                  width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                  border: `2px solid ${diffReturn ? 'var(--ac)' : 'var(--bd)'}`,
                  background: diffReturn ? 'var(--ac)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 10, fontWeight: 700,
                }}>{diffReturn ? '✓' : ''}</div>
                <span style={{ fontSize: 11 }}>Return at a different location</span>
              </div>

              {diffReturn && (
                <div>
                  <div className="sdiv" style={{ marginBottom: 10 }}>Return Location</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8 }}>
                    {locations.map(loc => (
                      <div key={loc.id} onClick={() => setReturnLocId(loc.id)} style={{
                        border: `1.5px solid ${returnLocId === loc.id ? 'var(--ac)' : 'var(--bd)'}`,
                        borderRadius: 'var(--r)', padding: '10px 12px', cursor: 'pointer',
                        background: returnLocId === loc.id ? 'var(--ac-l)' : 'var(--sur)', fontSize: 11,
                      }}>
                        <div style={{ fontWeight: 600, marginBottom: 3 }}>📍 {loc.name}</div>
                        {loc.fee > 0 && <div style={{ fontSize: 10, color: 'var(--am)', fontWeight: 600, marginTop: 4 }}>+€{loc.fee} fee</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {pickupLoc && (
                <div style={{ background: 'var(--bl-l)', border: '1px solid var(--bl-b)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: 11 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>📍 Location Summary</div>
                  <div>Pickup: <strong>{pickupLoc.name}</strong> — {locFee > 0 ? `€${locFee} fee` : 'Free'}</div>
                  {diffReturn && returnLoc && <div style={{ marginTop: 3 }}>Return: <strong>{returnLoc.name}</strong></div>}
                </div>
              )}
            </div>
          )}

          {/* ── STEP 2: Extras & Insurance ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Extras</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {extras.filter(e => e.enabled !== false).map(ex => {
                    const qty       = selectedExtras[ex.id] || 0;
                    const sel       = qty > 0;
                    const unitPrice = ex.perDay ? ex.price * days : ex.price;
                    const totalPrice = unitPrice * (ex.allowQty ? qty : 1);
                    return (
                      <div key={ex.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 12px',
                        border: `1.5px solid ${sel ? 'var(--gr)' : 'var(--bd)'}`,
                        borderRadius: 'var(--r)',
                        background: sel ? 'var(--gr-l)' : 'var(--sur)',
                      }}>
                        <span style={{ fontSize: 20 }}>{ex.icon || '➕'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 11, fontWeight: 500 }}>{ex.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                            €{ex.price}/{ex.perDay ? 'day' : 'flat'}
                            {sel && <span style={{ color: 'var(--gr)', fontWeight: 600 }}> → €{totalPrice} total</span>}
                          </div>
                        </div>
                        {ex.allowQty ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1.5px solid var(--bd)', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                            <button onClick={() => setExtraQty(ex.id, qty - 1, ex.maxQty)} style={{
                              width: 30, height: 32, background: qty > 0 ? 'var(--sur2)' : 'var(--sur)',
                              border: 'none', fontSize: 16, cursor: qty > 0 ? 'pointer' : 'default',
                              color: qty > 0 ? 'var(--tx)' : 'var(--tx3)',
                            }}>−</button>
                            <div style={{ width: 30, textAlign: 'center', fontSize: 13, fontWeight: 600, background: 'var(--sur)' }}>{qty}</div>
                            <button onClick={() => setExtraQty(ex.id, qty + 1, ex.maxQty)} style={{
                              width: 30, height: 32, background: qty < (ex.maxQty || 10) ? 'var(--sur2)' : 'var(--sur)',
                              border: 'none', fontSize: 16, cursor: qty < (ex.maxQty || 10) ? 'pointer' : 'default',
                              color: qty < (ex.maxQty || 10) ? 'var(--tx)' : 'var(--tx3)',
                            }}>+</button>
                          </div>
                        ) : (
                          <button onClick={() => toggleExtra(ex.id)} style={{
                            width: 32, height: 32, borderRadius: 8, border: `1.5px solid ${sel ? 'var(--gr)' : 'var(--bd)'}`,
                            background: sel ? 'var(--gr)' : 'var(--sur)', color: sel ? '#fff' : 'var(--tx3)',
                            fontSize: 16, cursor: 'pointer', flexShrink: 0,
                          }}>{sel ? '✓' : '+'}</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Insurance <span style={{ fontSize: 9, color: 'var(--rd)', fontWeight: 600 }}>Required</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 8 }}>
                  {vehicleInsPlans.map(ins => {
                    const sel   = selectedInsurance === ins.id;
                    const total = (ins.price || 0) * days;
                    return (
                      <div key={ins.id} onClick={() => setSelectedInsurance(ins.id)} style={{
                        border: `1.5px solid ${sel ? 'var(--ac)' : 'var(--bd)'}`,
                        borderRadius: 'var(--r)', padding: '12px', cursor: 'pointer',
                        background: sel ? 'var(--ac-l)' : 'var(--sur)',
                        position: 'relative',
                      }}>
                        {ins.recommended && <div style={{ position: 'absolute', top: 6, right: 6, fontSize: 8, background: 'var(--ac)', color: '#fff', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>Recommended</div>}
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{ins.icon}</div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{ins.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--bl)', fontWeight: 600, marginTop: 2 }}>
                          {ins.price > 0 ? `€${ins.price}/day` : 'Included'}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>€{total} total · Excess: {ins.excess}</div>
                        <div style={{ marginTop: 4 }}>
                          {ins.deposit > 0
                            ? <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                background: '#fef2f2', border: '1px solid #fca5a5',
                                borderRadius: 5, padding: '2px 7px',
                                fontSize: 10, color: '#b91c1c', fontWeight: 700,
                              }}>🔒 Deposit: €{ins.deposit}</span>
                            : <span style={{ fontSize: 9, color: 'var(--gr)', fontWeight: 600 }}>🔒 No deposit</span>
                          }
                        </div>
                        {ins.desc && <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 5, lineHeight: 1.4 }}>{ins.desc}</div>}
                        {sel && <div style={{ marginTop: 6, fontSize: 9, background: 'var(--ac)', color: '#fff', padding: '2px 7px', borderRadius: 8, display: 'inline-block', fontWeight: 600 }}>Selected ✓</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Price Breakdown</div>
                {(() => {
                  const f = vatMode === 'inclusive' ? 1 / (1 + vatRate) : 1;
                  const rateEx = (parseFloat(draft.rate || 0) * f).toFixed(2);
                  return <>
                    <Row label={`Vehicle (${days}d × €${rateEx})`} val={baseSub * f} />
                    {selectedIns && insTotal > 0 && <Row label={`Insurance — ${selectedIns.name}`} val={insTotal * f} />}
                    {extras.filter(ex => (selectedExtras[ex.id] || 0) > 0).map(ex => {
                      const qty = selectedExtras[ex.id];
                      const lineTotal = (ex.perDay ? ex.price * days : ex.price) * qty;
                      const lbl = ex.perDay
                        ? `${ex.name} (${qty} × €${(ex.price * f).toFixed(2)}/d × ${days}d)`
                        : `${ex.name} (${qty} × €${(ex.price * f).toFixed(2)})`;
                      return <Row key={ex.id} label={lbl} val={lineTotal * f} />;
                    })}
                    {locFee > 0 && <Row label="Location fee" val={locFee * f} />}
                  </>;
                })()}
                <Row label={`VAT (${Math.round(vatRate*100)}%)`} val={vat} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 13, borderTop: '1px solid var(--bd)', paddingTop: 6, marginTop: 4 }}>
                  <span>Total to pay</span>
                  <span style={{ color: 'var(--br)' }}>€{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: ID & Photos ── */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Customer Identification</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div className="fg">
                    <label className="fl">Passport / ID No.</label>
                    <input type="text" className="fi" value={ciPassport} onChange={e => setCiPassport(e.target.value)}
                      style={{ fontFamily: "'DM Mono',monospace" }} placeholder="P1234567" />
                  </div>
                  <div className="fg">
                    <label className="fl">Driving Licence No.</label>
                    <input type="text" className="fi" value={ciLicence} onChange={e => setCiLicence(e.target.value)}
                      style={{ fontFamily: "'DM Mono',monospace" }} placeholder="CY-DL-123456" />
                  </div>
                  <div className="fg">
                    <label className="fl">Licence Expiry</label>
                    <input type="date" className="fi" value={ciLicExpiry} onChange={e => setCiLicExpiry(e.target.value)} />
                  </div>
                </div>

                <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '10px 14px', marginTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8 }}>Verification Checklist</div>
                  {[
                    [passportChecked, setPassportChecked, 'Passport / ID checked — matches booking name'],
                    [licenceChecked,  setLicenceChecked,  'Driving licence valid — not expired'],
                    ...(depositAmt > 0 ? [[depositChecked, setDepositChecked, `Deposit collected — €${depositAmt} (${selectedIns?.name || 'insurance'})`]] : []),
                  ].map(([checked, setChecked, label]) => (
                    <div key={label} onClick={() => setChecked(c => !c)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, marginBottom: 6 }}>
                      <div style={{
                        width: 16, height: 16, borderRadius: 3, flexShrink: 0,
                        border: `2px solid ${checked ? 'var(--gr)' : 'var(--bd)'}`,
                        background: checked ? 'var(--gr)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 10, fontWeight: 700,
                      }}>{checked ? '✓' : ''}</div>
                      {label}
                    </div>
                  ))}
                </div>
              </div>

              {/* ID Document photos — pre-filled from booking, replaceable */}
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>
                  Identity Documents
                  {(ciPassportPhoto || ciLicencePhoto) && (
                    <span style={{ fontSize: 9, background: 'var(--bl)', color: '#fff', padding: '1px 7px', borderRadius: 8, marginLeft: 8, fontWeight: 600 }}>
                      Photos from booking
                    </span>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <DocPhoto icon="🪪" label="Passport / ID" photo={ciPassportPhoto} setPhoto={setCiPassportPhoto} fileRef={passportFileRef} />
                  <DocPhoto icon="🚗" label="Driving Licence" photo={ciLicencePhoto} setPhoto={setCiLicencePhoto} fileRef={licenceFileRef} />
                </div>
              </div>

              {/* ── Pre-existing damages on this vehicle ─────────────── */}
              {(() => {
                if (!vehicle || !getDamagesForVehicle) return null;
                const all = getDamagesForVehicle(vehicle.id);
                // Pre-existing = physically still on the car (not yet repaired)
                // Legacy fallback: old records that only had status='resolved' are treated as repaired
                const preExisting = all.filter(d => !d.repaired && d.status !== 'resolved');
                if (preExisting.length === 0) return null;
                return (
                  <div style={{
                    background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
                    padding: '12px 14px',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                      ⚠️ Pre-existing damages on this vehicle ({preExisting.length})
                    </div>
                    <div style={{ fontSize: 10, color: '#92400e', marginBottom: 10 }}>
                      Verify these were on the car <strong>before handover</strong> — do not charge the new customer for them on return.
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {preExisting.map(d => {
                        const sevColor = d.severity === 'major' ? '#ef4444'
                                      : d.severity === 'moderate' ? '#f59e0b'
                                      : '#10b981';
                        const cover = (d.photos || [])[0];
                        return (
                          <div key={d.id} style={{
                            background: '#fff', border: '1px solid #fde68a', borderRadius: 6,
                            padding: '8px 10px', display: 'flex', gap: 10, alignItems: 'center',
                          }}>
                            {cover ? (
                              <img src={cover} alt="damage" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                            ) : (
                              <div style={{ width: 48, height: 48, background: '#fde68a', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📷</div>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: sevColor }} />
                                {d.location || 'Damage'}
                                <span style={{ fontSize: 9, color: '#92400e', fontWeight: 400 }}>
                                  · reported {d.reportedDate ? new Date(d.reportedDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                                </span>
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {d.description || '—'}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                              <button
                                type="button"
                                onClick={() => openModal('damage', d.id)}
                                style={{ fontSize: 10, padding: '4px 8px', background: '#fff', border: '1px solid #fde68a', borderRadius: 4, cursor: 'pointer', color: '#92400e' }}
                              >
                                View
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (!window.confirm('Mark this damage as repaired? It will no longer show as pre-existing on future rentals.')) return;
                                  updateDamage(d.id, { repaired: true, repairedDate: new Date().toISOString().slice(0, 10) });
                                  showToast('Damage marked as repaired');
                                }}
                                style={{ fontSize: 10, padding: '4px 8px', background: '#10b981', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff' }}
                              >
                                ✓ Repaired
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Vehicle condition */}
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Vehicle Condition at Pickup</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                  {COND_OPTIONS.map(opt => (
                    <div key={opt.value} onClick={() => setCiCond(opt.value)} style={{
                      padding: '8px 6px', textAlign: 'center', fontSize: 10,
                      border: `1.5px solid ${ciCond === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                      borderRadius: 'var(--r)', cursor: 'pointer',
                      background: ciCond === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                    }}>
                      {opt.label}
                    </div>
                  ))}
                </div>
                <div className="fg" style={{ marginTop: 8 }}>
                  <label className="fl">Condition notes</label>
                  <textarea className="fi" rows={2} value={ciCondNote} onChange={e => setCiCondNote(e.target.value)} placeholder="Describe any existing damage..." />
                </div>

                {/* Log damage found at pickup — when scratch/damage/major selected,
                    record it so it's documented as pre-existing (the returning
                    customer isn't blamed for it). Also lists records already
                    linked to this rental. */}
                {(() => {
                  const linkedDmgs = getDamagesForRental ? (getDamagesForRental(draft?.id) || []) : [];
                  const condTriggers = ciCond === 'small_scratch' || ciCond === 'damage' || ciCond === 'major_damage';
                  if (!condTriggers && linkedDmgs.length === 0) return null;
                  const presetSeverity = ciCond === 'major_damage' ? 'major'
                                       : ciCond === 'damage'       ? 'moderate'
                                       : 'minor';
                  return (
                    <div style={{ marginTop: 10, background: 'var(--ac-l)', border: '1px solid var(--ac-b)', borderRadius: 'var(--r)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: 11, color: 'var(--ac)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                        <div>
                          {linkedDmgs.length > 0
                            ? <>📋 <strong>{linkedDmgs.length} damage record{linkedDmgs.length === 1 ? '' : 's'}</strong> logged at this pickup</>
                            : <>📸 <strong>Log this damage</strong> as pre-existing so it's on file before handover</>}
                        </div>
                        <button
                          type="button" className="btn btn-primary btn-sm"
                          onClick={() => {
                            const cust = customers.find(c => `${c.fname} ${c.lname}`.trim() === (draft?.customer || '').trim());
                            openModal('damage', {
                              vid: draft.vid, rentalId: draft.id,
                              customerId: cust?.id || null,
                              customerName: draft?.customer || '',
                              description: ciCondNote || '',
                              severity: presetSeverity,
                              paidBy: 'absorbed',          // pre-existing at pickup → not charged to incoming customer
                              status: 'reported',
                              foundAt: 'pickup',
                            });
                          }}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          + Damage Report
                        </button>
                      </div>
                      {linkedDmgs.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {linkedDmgs.map(d => (
                            <button key={d.id} type="button" onClick={() => openModal('damage', d.id)}
                              style={{ background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 6, padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, cursor: 'pointer', textAlign: 'left' }}>
                              <span>{d.location || 'Damage'} — {d.description?.slice(0, 50)}{d.description?.length > 50 ? '…' : ''}</span>
                              <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 600 }}>€{(d.finalCost ?? d.estimatedCost ?? 0).toFixed(0)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Pickup photos */}
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>
                  Pickup Photos
                  <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)', marginLeft: 8 }}>
                    {Object.values(pickupPhotos).filter(Boolean).length}/{PHOTO_SLOTS.length} added
                  </span>
                </div>
                {/* Hidden file input shared by all slots */}
                <input ref={pickupPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onSlotFile} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  {PHOTO_SLOTS.map(label => (
                    <div key={label} style={{
                      border: `2px ${pickupPhotos[label] ? 'solid var(--gr-b)' : 'dashed var(--bd)'}`,
                      borderRadius: 'var(--r)', overflow: 'hidden',
                      background: pickupPhotos[label] ? 'transparent' : 'var(--sur2)',
                    }}>
                      {pickupPhotos[label] ? (
                        <>
                          <img
                            src={pickupPhotos[label]}
                            alt={label}
                            style={{ width: '100%', height: 88, objectFit: 'cover', display: 'block', cursor: 'pointer' }}
                            onClick={() => window.open(pickupPhotos[label], '_blank')}
                          />
                          <div style={{ padding: '4px 4px 5px', display: 'flex', gap: 3, justifyContent: 'center' }}>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotFile(label)}>📁</button>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9 }} onClick={() => openSlotCamera(label)}>📷</button>
                            <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 9, color: 'var(--ac)' }} onClick={() => removeSlotPhoto(label)}>✕</button>
                          </div>
                          <div style={{ textAlign: 'center', fontSize: 9, color: 'var(--gr)', paddingBottom: 4, fontWeight: 600 }}>✓ {label}</div>
                        </>
                      ) : (
                        <div
                          style={{ padding: '18px 8px', textAlign: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}
                          onClick={() => openSlotFile(label)}
                        >
                          <div style={{ fontSize: 22, marginBottom: 4 }}>📷</div>
                          <div style={{ fontWeight: 500, marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 9 }}>+ Add photo</div>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ marginTop: 6, fontSize: 9, padding: '2px 8px' }}
                            onClick={e => { e.stopPropagation(); openSlotCamera(label); }}
                          >
                            📸 Camera
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 4: Sign & Pay ── */}
          {step === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Agreement — full formatted */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div className="sdiv" style={{ marginBottom: 0 }}>Rental Agreement</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => setShowFullAgreement(v => !v)}>
                      {showFullAgreement ? '▲ Collapse' : '📄 View'}
                    </button>
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => {
                        const { tmpl, ctx } = getCheckinContract();
                        if (tmpl) printContract(tmpl, ctx, { title: `Contract ${draft.id}` });
                        else showToast('No agreement template configured for this vehicle');
                      }}>
                      🖨️ Print
                    </button>
                  </div>
                </div>

                {/* Always-visible summary */}
                <div style={{
                  background: 'var(--sur2)', border: '1px solid var(--bd)',
                  borderRadius: 'var(--r)', padding: '10px 14px', fontSize: 11, lineHeight: 1.7,
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 14px', fontSize: 11 }}>
                    <div><span style={{ color: 'var(--tx3)' }}>Contract:</span> <strong>{draft.id}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Date:</span> <strong>{isoNow}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Customer:</span> <strong>{draft.customer}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Passport/ID:</span> <strong>{ciPassport || draft.passport || '—'}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Vehicle:</span> <strong>{vehicle?.name}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Plate:</span> <strong>{vehicle?.plate}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Pickup:</span> <strong>{draft.startLabel}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Return:</span> <strong>{draft.endLabel}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Duration:</span> <strong>{days} days</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Agreed Total:</span> <strong style={{ color: 'var(--gr)' }}>€{grandTotal.toFixed(2)}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Deposit:</span> <strong>€{draft.deposit || 0}</strong></div>
                    <div><span style={{ color: 'var(--tx3)' }}>Insurance:</span> <strong>{selectedIns?.name || '—'}</strong></div>
                    {pickupLoc && <div><span style={{ color: 'var(--tx3)' }}>Pickup loc:</span> <strong>{pickupLoc.name}</strong></div>}
                    {diffReturn && returnLoc && <div><span style={{ color: 'var(--tx3)' }}>Return loc:</span> <strong>{returnLoc.name}</strong></div>}
                  </div>
                </div>

                {/* Full agreement document — shared renderer */}
                {showFullAgreement && (() => {
                  const tmpl = getAgreementForVehicle(draft?.vid);
                  const ciCtx = buildContractContext({
                    rental: {
                      id: draft.id, customer: draft.customer,
                      passport: ciPassport || draft.passport,
                      licence: ciLicence || draft.licence,
                      email: draft.email, phone: draft.phone,
                      startLabel: draft.startLabel, endLabel: draft.endLabel,
                      t1: draft.t1, t2: draft.t2,
                      pickupLocName: pickupLoc?.name,
                      returnLocName: diffReturn ? returnLoc?.name : pickupLoc?.name,
                      calSpan: days, rate: draft.rate,
                      deposit: depositAmt, depositMethod: ciPayMethod,
                      agreedPrice: parseFloat(grandTotal.toFixed(2)),
                      insurance: selectedIns?.name, insuranceExcess: selectedIns?.excess,
                      extras: extras.filter(e => (selectedExtras[e.id] || 0) > 0).map(e => e.name),
                    },
                    vehicle, company: companySettings,
                    signatureDataUrl: draft?.presignData || '',
                    signedAt: draft?.presignedAt || '',
                  });
                  return (
                    <div style={{ marginTop: 8, maxHeight: 360, overflowY: 'auto', border: '1px solid var(--bd)', borderRadius: 'var(--r)', background: 'var(--sur2)', padding: 10 }}>
                      {tmpl
                        ? <ContractDocument template={tmpl} context={ciCtx} paperLook={true} />
                        : <div style={{ padding: 20, color: 'var(--tx3)', fontSize: 11 }}>No agreement template configured for this vehicle.</div>}
                    </div>
                  );
                })()}
              </div>

              {/* Signature */}
              <div>
                <div className="sdiv" style={{ marginBottom: 10 }}>Customer Signature</div>

                {/* Show pre-sign if exists */}
                {draft.presigned && draft.presignData && !ciSigned && (
                  <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '8px 12px', marginBottom: 8, fontSize: 11 }}>
                    <div style={{ color: 'var(--gr)', fontWeight: 600, marginBottom: 6 }}>✅ Pre-signed online — {draft.presignedAt || ''}</div>
                    <img src={draft.presignData} alt="Pre-signature"
                      style={{ width: '100%', background: '#fff', border: '1px solid var(--bd)', borderRadius: 6, maxHeight: 70, objectFit: 'contain' }} />
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 6 }}>Customer signed the agreement digitally. You may collect a fresh in-person signature below if preferred.</div>
                  </div>
                )}

                <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'hidden', position: 'relative' }}>
                  <canvas
                    ref={canvasRef}
                    width={700} height={110}
                    style={{ display: 'block', width: '100%', height: 110, cursor: 'crosshair', background: '#fafafa', touchAction: 'none' }}
                    onMouseDown={canvasDown}
                    onMouseMove={canvasMove}
                    onMouseUp={canvasUp}
                    onMouseLeave={canvasUp}
                    onTouchStart={e => { e.preventDefault(); canvasDown(e.touches[0]); }}
                    onTouchMove={e => { e.preventDefault(); canvasMove(e.touches[0]); }}
                    onTouchEnd={canvasUp}
                  />
                  {!ciSigned && (
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                      color: 'var(--tx3)', fontSize: 11, pointerEvents: 'none',
                    }}>Sign here →</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={clearCanvas}>Clear</button>
                  {ciSigned && <span style={{ fontSize: 10, background: 'var(--gr)', color: '#fff', padding: '2px 9px', borderRadius: 10, fontWeight: 600 }}>Signed ✓</span>}
                </div>
              </div>

              {/* Final breakdown */}
              {isPrepaid ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {/* Already paid */}
                  <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gr)', marginBottom: 6 }}>✅ Already Paid</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--tx2)' }}>{existingInv?.methodLabel || (draft?.payMethod === 'stripe' ? '💳 Card (Stripe)' : draft?.payMethod === 'viva' ? '🔵 Viva Wallet' : draft?.payMethod === 'paypal' ? '🅿️ PayPal' : 'Online payment')}</span>
                      <span style={{ fontWeight: 700, color: 'var(--gr)' }}>€{alreadyPaid.toFixed(2)}</span>
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 2 }}>
                      {existingInv ? `${invoiceDisplayNumber(existingInv)} · ${existingInv.issue}` : 'Paid at time of booking'}
                    </div>
                  </div>

                  {/* Additional charges at desk */}
                  {dueAtDesk > 0 ? (
                    <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 10 }}>
                        🧾 {diffOwed > 0 ? 'Date Change — Difference Due' : 'Additional Charges at Check-in'}
                      </div>
                      {diffOwed > 0 && (
                        <div style={{ fontSize: 10, color: 'var(--am)', background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 4, padding: '5px 8px', marginBottom: 8 }}>
                          ⚠ Customer extended the rental — collect the difference at pickup
                        </div>
                      )}
                      {/* Itemized breakdown of what makes up the difference */}
                      {(() => {
                        const deskLines = computeDeskLines();
                        if (deskLines.length === 0) return null;
                        return (
                          <div style={{ marginBottom: 8, paddingBottom: 6, borderBottom: '1px dashed var(--bd)' }}>
                            {deskLines.map((l, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '2px 0' }}>
                                <span style={{ color: 'var(--tx2)' }}>
                                  {l.desc}{l.qty > 1 ? <span style={{ color: 'var(--tx3)' }}> · {l.qty} × €{(+l.unit).toFixed(2)}</span> : ''}
                                </span>
                                <span style={{ fontWeight: 600 }}>€{(+l.total).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      <Row label="Updated booking total" val={grandTotal} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, color: 'var(--gr)', fontSize: 11 }}>
                        <span>Already paid{existingInv ? ` (${invoiceDisplayNumber(existingInv)})` : ''}</span>
                        <span>−€{alreadyPaid.toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, borderTop: '1px solid var(--bd)', paddingTop: 6, marginTop: 4 }}>
                        <span>Due at desk</span>
                        <span style={{ color: 'var(--br)' }}>€{dueAtDesk.toFixed(2)}</span>
                      </div>
                      {/* View / Print pro forma for the difference */}
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 8 }}>
                        <button className="btn btn-ghost btn-sm"
                          onClick={() => printInvoice(buildDeskProforma(), companySettings, rentals, vehicles)}>
                          📄 View / Print pro forma
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', fontSize: 11, color: 'var(--tx3)', textAlign: 'center' }}>
                      ✓ Fully paid — nothing due at desk (extras still collected separately if added)
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gr)', marginBottom: 8 }}>✅ Final Total</div>
                  {(() => {
                    const f = vatMode === 'inclusive' ? 1 / (1 + vatRate) : 1;
                    const rateEx = (parseFloat(draft.rate || 0) * f).toFixed(2);
                    return <>
                      <Row label={`Vehicle (${days}d × €${rateEx})`} val={baseSub * f} />
                      {selectedIns && insTotal > 0 && <Row label={`Insurance: ${selectedIns.name}`} val={insTotal * f} />}
                      {extras.filter(ex => (selectedExtras[ex.id] || 0) > 0).map(ex => {
                        const qty = selectedExtras[ex.id];
                        const lineTotal = (ex.perDay ? ex.price * days : ex.price) * qty;
                        const lbl = ex.perDay
                          ? `${ex.name} (${qty} × €${(ex.price * f).toFixed(2)}/d × ${days}d)`
                          : `${ex.name} (${qty} × €${(ex.price * f).toFixed(2)})`;
                        return <Row key={ex.id} label={lbl} val={lineTotal * f} />;
                      })}
                      {locFee > 0 && <Row label="Location fee" val={locFee * f} />}
                    </>;
                  })()}
                  <Row label={`VAT (${Math.round(vatRate*100)}%)`} val={vat} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 14, borderTop: '1px solid var(--gr-b)', paddingTop: 6, marginTop: 4 }}>
                    <span>Due at desk</span>
                    <span style={{ color: 'var(--br)' }}>€{grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* Payment method — applies ONLY to the amount collected at the desk.
                  Sits below the breakdown so it's clearly about "Due at desk". */}
              {draft.bankTransferVerified && dueAtDesk <= 0 ? (
                <div style={{ marginTop: 12, background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 28 }}>🏦</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gr)' }}>✅ Bank Transfer — Verified</div>
                    <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>Payment was confirmed in the Incoming page. Nothing to collect at the desk.</div>
                  </div>
                </div>
              ) : dueAtDesk > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div className="sdiv" style={{ marginBottom: 4 }}>
                    {isPrepaid ? `Payment for amount due at desk · €${dueAtDesk.toFixed(2)}` : 'Payment Method'}
                  </div>
                  {isPrepaid && (
                    <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 10 }}>
                      The €{alreadyPaid.toFixed(2)} already paid stays on its own pro forma. This collects only the remaining balance.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                    {[
                      { value: 'cash',    icon: '💵', label: 'Cash' },
                      { value: 'stripe',  icon: '💳', label: 'Card (Stripe)' },
                      { value: 'bank',    icon: '🏦', label: 'Bank Transfer' },
                      { value: 'prepaid', icon: '✅', label: 'Pre-paid Online' },
                    ].map(opt => (
                      <div key={opt.value} onClick={() => setCiPayMethod(opt.value)} style={{
                        border: `1.5px solid ${ciPayMethod === opt.value ? 'var(--ac)' : 'var(--bd)'}`,
                        borderRadius: 'var(--r)', padding: '10px 8px', cursor: 'pointer', textAlign: 'center',
                        background: ciPayMethod === opt.value ? 'var(--ac-l)' : 'var(--sur)',
                      }}>
                        <div style={{ fontSize: 22, marginBottom: 4 }}>{opt.icon}</div>
                        <div style={{ fontSize: 10, fontWeight: 500 }}>{opt.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 12, background: 'var(--gr-l)', border: '1px solid var(--gr-b)', borderRadius: 'var(--r)', padding: '10px 14px', fontSize: 11, color: 'var(--gr)', fontWeight: 600, textAlign: 'center' }}>
                  ✓ Fully paid — nothing to collect at the desk
                </div>
              )}
            </div>
          )}

        </ModalBody>

        <ModalFooter>
          <span style={{ fontSize: 10, color: 'var(--tx3)', marginRight: 'auto' }}>Step {step} of {CI_STEPS.length}</span>
          <button className="btn btn-ghost" onClick={() => step > 1 ? setStep(s => s - 1) : closeModal('check-in')}>
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          {step < 4
            ? <button className="btn btn-primary" onClick={nextStep}>Next →</button>
            : <button className="btn btn-primary" style={{ background: 'var(--gr)' }} onClick={finalize}>✅ Complete Check-in</button>
          }
        </ModalFooter>
      </Modal>

      {/* Camera overlay — rendered outside modal so z-index works */}
      {camera && (
        <CameraCapture
          label={camera.label}
          onCapture={dataUrl => { camera.setPhoto(dataUrl); setCamera(null); }}
          onClose={() => setCamera(null)}
        />
      )}
    </>
  );
}
